import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSeller } from '../api';
import VerifyOtpModal from './VerifyOtpModal';
import { FaSpinner } from 'react-icons/fa';
import axios from 'axios';

const CreateAccountForm = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [emailsent, setemailsent] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [saleType, setSaleType] = useState('Sale By Seller');

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    companyName: '',
    gstNumber: '',
    pan: '',
    street: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    password: '',
    confirmPassword: '',
    role: 'Sale By Seller',
    latitude: '',
    longitude: '',
  });

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Only allow digits for mobile
    if (name === 'mobile' && (value.length > 10 || !/^\d*$/.test(value))) {
      return;
    }

    setFormData({ ...formData, [name]: value });
    if (name === 'email') setemailsent(value);

    if (name === 'role') {
      setSaleType(value);
      setTermsAccepted(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Full name is required';
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) return 'Valid email is required';
    if (!formData.mobile.trim() || formData.mobile.length !== 10) return 'Valid 10-digit mobile number is required';
    if (!formData.companyName.trim()) return 'Company name is required';
    if (!formData.gstNumber.trim() || !/^[A-Za-z0-9]+$/.test(formData.gstNumber)) return 'GST number must be alphanumeric';
    if (!formData.pan.trim()) return 'PAN number is required';
    if (!formData.street.trim()) return 'Street address is required';
    if (!formData.city.trim()) return 'City is required';
    if (!formData.state.trim()) return 'State is required';
    if (!formData.country.trim()) return 'Country is required';
    if (!formData.postalCode.trim() || !/^\d+$/.test(formData.postalCode)) return 'Postal code must be numeric';
    if (!formData.password.trim()) return 'Password is required';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    if (!formData.latitude || !formData.longitude) return 'Location details are required';
    if (!termsAccepted) return 'You must accept the terms and conditions';
    return null;
  };

  const handleProceedToTerms = () => {
    if (saleType === 'Sale By Seller') {
      navigate('/sales-with-ogcs', { state: { formData } });
    } else if (saleType === 'Sale By OGCS') {
      navigate('/sales-with-ogcs', { state: { formData } });
    } else if (saleType === 'Both') {
      navigate('/sales-with-ogcs', { state: { formData } });
    }
  };

  const fetchCityFromLatLng = async (lat, lon) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=en`
      );
      const data = response.data;
      const city =
        data?.address?.city ||
        data?.address?.town ||
        data?.address?.village ||
        data?.address?.state ||
        'Unknown City';

      setFormData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lon,
        city,
      }));
    } catch (error) {
      console.error('Error fetching city:', error);
    }
  };

  const handleSearchPlace = async (query) => {
    setSearchQuery(query);

    if (!query) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          format: 'json',
          q: query,
        },
      });

      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching place:', error);
    }
  };

  const handlePlaceSelection = (place) => {
    fetchCityFromLatLng(place.lat, place.lon);
    setSearchQuery(place.display_name);
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setLoading(true);
    try {
      const data = await createSeller(formData);
      if (data?.status === 201) handleOpenDialog();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <VerifyOtpModal open={isDialogOpen} onClose={handleCloseDialog} email={emailsent} />
      <div className="w-full max-w-4xl">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-gray-700">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
                className="w-full p-2 border rounded-lg"
              />
              {/* Live invoice name note */}
              {formData.name.trim().length > 0 && (
                <p className="mt-1 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                   Enter the legal name you want to associate with your account and invoices: <span className="font-medium">{formData.name}</span>
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* Mobile */}
            <div>
              <label htmlFor="mobile" className="block text-gray-700">Mobile</label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                placeholder="Enter your mobile number"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className="block text-gray-700">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                placeholder="Enter your company name"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* GST */}
            <div>
              <label htmlFor="gstNumber" className="block text-gray-700">GST Number</label>
              <input
                type="text"
                id="gstNumber"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                required
                placeholder="Enter GST number"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* PAN */}
            <div>
              <label htmlFor="pan" className="block text-gray-700">PAN</label>
              <input
                type="text"
                id="pan"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                required
                placeholder="Enter PAN number"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* Address Fields */}
            <div className="flex space-x-6">
              <div className="w-1/2">
                <label htmlFor="street" className="block text-gray-700">Street</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  placeholder="Enter street address"
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div className="w-1/2">
                <label htmlFor="city" className="block text-gray-700">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="Enter city"
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex space-x-6">
              <div className="w-1/2">
                <label htmlFor="state" className="block text-gray-700">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  placeholder="Enter state"
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div className="w-1/2">
                <label htmlFor="country" className="block text-gray-700">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder="Enter country"
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex space-x-6">
              <div className="w-1/2">
                <label htmlFor="postalCode" className="block text-gray-700">Postal Code</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="Enter postal code"
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div className="w-1/2 mt-2">
                <label htmlFor="role" className="block text-gray-700">Type</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="select">Select</option>
                  <option value="Sale By Seller">Sale By Seller</option>
                  <option value="Sale By OGCS">Sale By OGCS</option>
                  <option value="Both">Both</option>
                </select>
              </div>
            </div>

            {/* Password Fields */}
            <div>
              <label htmlFor="password" className="block text-gray-700">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter password"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-gray-700">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm password"
                className="w-full p-2 border rounded-lg"
              />
            </div>

            {/* Search Place */}
            <div>
              <label htmlFor="searchPlace" className="block text-gray-700">Search Place</label>
              <input
                type="text"
                id="searchPlace"
                value={searchQuery}
                onChange={(e) => handleSearchPlace(e.target.value)}
                placeholder="Search for a place"
                className="w-full p-2 border rounded-lg"
              />
              {searchResults.length > 0 && (
                <ul className="bg-white border rounded-lg max-h-40 overflow-y-auto mt-2">
                  {searchResults.map((place, index) => (
                    <li
                      key={index}
                      className="p-2 hover:bg-gray-200 cursor-pointer"
                      onClick={() => handlePlaceSelection(place)}
                    >
                      {place.display_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {errorMessage && <p className="text-red-600 mt-2">{errorMessage}</p>}

          {/* Terms & Conditions */}
          <div className="mt-4 flex items-center space-x-2">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="terms" className="text-gray-800 text-sm">
              I agree to the{' '}
              <span className="text-blue-600 cursor-pointer" onClick={handleProceedToTerms}>
                Terms and Conditions
              </span>
              .
            </label>
          </div>

          <button
            type="submit"
            className={`mt-4 w-full text-white p-2 rounded-lg ${
              termsAccepted ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!termsAccepted}
          >
            {loading ? <FaSpinner className="animate-spin" /> : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountForm;
